import { useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  BarChart,
  Card,
  CardHeader,
  DataTable,
  DateRangePicker,
  EmptyState,
  ErrorState,
  FilterChip,
  Grid,
  KpiCard,
  PageHeader,
  Row,
  SegmentedControl,
  TableCard,
} from '@sparkery/ui';
import type { RangeKey } from '@sparkery/contracts';
import {
  RANGE_OPTIONS,
  conversionText,
  moneyShort,
  orderColumns,
  peakOf,
  ratePercent,
  useReport,
} from '../../lib/report.tsx';

type View = 'traffic' | 'sales';

const VIEW_OPTIONS: { value: View; label: string }[] = [
  { value: 'traffic', label: 'Traffic' },
  { value: 'sales', label: 'Sales' },
];

/**
 * Traffic and sales, side by side but never conflated.
 *
 * The two are measured differently — traffic by UTM in GA4, sales by discount code in
 * Shopify — and they will not reconcile: a visitor can buy days later on another
 * device, and a code can be used by someone who never clicked a link. The view says so
 * rather than inviting the arithmetic.
 */
export function ReportingView() {
  const [view, setView] = useState<View>('traffic');
  const [range, setRange] = useState<RangeKey>('7d');
  const today = new Date().toISOString().slice(0, 10);
  const [custom, setCustom] = useState(() => ({ from: daysBefore(today, 29), to: today }));
  // Set when staff arrive from a row in Partners. Ignored by the server for a partner
  // user, so it is passed through rather than guarded here.
  const { partnerId } = useSearch({ from: '/reporting' });
  const navigate = useNavigate({ from: '/reporting' });

  const report = useReport({
    range,
    ...(range === 'custom' ? { from: custom.from, to: custom.to } : {}),
    ...(partnerId ? { partnerId } : {}),
  });
  const data = report.data;
  // Assumed present until the report says otherwise, so the table does not reflow a
  // moment after it renders. See DashboardView.
  const showsCommission = data?.showsCommission ?? true;
  // And withheld entirely on a failure with nothing cached: the assumption above stops
  // being transient once the request has failed, which would leave a fixed-fee partner
  // looking at Payable and Forecasted tiles full of em dashes. See DashboardView.
  const failed = report.isError && !data;

  const columns = orderColumns({ showsCommission, range });

  return (
    <>
      <PageHeader
        title="Reporting"
        // Whose numbers these are comes FIRST. Staff arriving from Partners are
        // looking at someone else's data, and a date range alone would not say so.
        subtitle={
          data
            ? [data.partnerName ?? 'All partners', `${data.from} to ${data.to}`].join(' · ')
            : // Not "Sales, commission and traffic": this fallback shows before the
              // report lands and whenever it fails, and a fixed-fee partner has no
              // commission on this page at all.
              'Sales and traffic'
        }
        actions={
          <Row gap="12px">
            {partnerId ? (
              <FilterChip
                label={data?.partnerName ?? 'Partner filter'}
                onClear={() => void navigate({ search: { partnerId: undefined } })}
              />
            ) : null}
            <SegmentedControl
              label="Report"
              options={VIEW_OPTIONS}
              value={view}
              onChange={setView}
            />
            <DateRangePicker
              value={range}
              options={[...RANGE_OPTIONS, { value: 'custom' as const, label: 'Custom' }]}
              onChange={setRange}
              customValue="custom"
              {...(range === 'custom' ? { label: `${custom.from} to ${custom.to}` } : {})}
              from={custom.from}
              to={custom.to}
              onRangeChange={setCustom}
              max={today}
            />
          </Row>
        }
      />

      {report.isError ? (
        <ErrorState
          title="Could not load the report"
          {...(report.error instanceof Error ? { detail: report.error.message } : {})}
          onRetry={() => void report.refetch()}
        />
      ) : null}

      {failed ? null : view === 'traffic' ? (
        <TrafficPanel data={data} loading={report.isPending} />
      ) : (
        <>
          {/* Three tiles for a fixed-fee partner: their sales, their orders and how
              well their traffic converts. Nothing about a percentage they do not earn. */}
          <Grid cols={showsCommission ? 4 : 3}>
            <KpiCard
              label="Referral sales"
              value={data ? moneyShort(data.summary.sales.amount) : '—'}
              meta="Store currency, after refunds"
              provisional={data?.quality.provisional ?? false}
              iconName="sales"
            />
            <KpiCard
              label="Orders"
              value={data ? String(data.summary.orderCount) : '—'}
              unit="orders"
              iconName="orders"
              tint="ink"
            />
            {/* The split gets two tiles here, where there is room for it. Together they
                are the period's commission; apart, they are the only two figures worth
                acting on — one can be invoiced, the other cannot yet. */}
            {showsCommission ? (
              <KpiCard
                label="Payable"
                value={data ? moneyShort(data.summary.payable.amount) : '—'}
                {...(data?.summary.commissionRate
                  ? { unit: `at ${ratePercent(data.summary.commissionRate)}%` }
                  : {})}
                tint="amber"
                meta="30-day window closed, net of claw-backs"
                iconName="commission"
              />
            ) : null}
            {showsCommission ? (
              <KpiCard
                label="Forecasted"
                value={data ? moneyShort(data.summary.forecasted.amount) : '—'}
                meta="Still inside its 30-day window"
                iconName="forecast"
                tint="pink"
              />
            ) : (
              <KpiCard
                label="Conversion rate"
                value={conversionText(data?.summary.conversionRate)}
                {...(data?.summary.conversionRate === null ? {} : { unit: '%' })}
                meta={
                  data?.summary.conversionRate === null
                    ? 'Needs visitor numbers to work out'
                    : 'Orders as a share of visits'
                }
                iconName="conversion"
                tint="amber"
              />
            )}
          </Grid>

          <Card>
            <CardHeader
              title="Referral sales"
              meta={
                (data ? peakOf(data.salesBars, { hourly: data.from === data.to }) : null) ??
                (data ? `${data.from} to ${data.to}` : undefined)
              }
            />
            <BarChart
              bars={data?.salesBars ?? []}
              caption="Referral sales over the selected period"
              emptyMessage="No referral sales in this period."
            />
          </Card>

          <TableCard title="Orders" meta={data ? `${data.orders.length} shown` : undefined}>
            <DataTable
              caption="Referral orders over the selected period"
              columns={columns}
              rows={data?.orders ?? []}
              rowKey={(row) => row.id}
              loading={report.isPending}
              empty={
                <EmptyState
                  title="No orders in this period"
                  body="No order used a referral code between these dates."
                />
              }
            />
          </TableCard>
        </>
      )}
    </>
  );
}

function daysBefore(isoDate: string, days: number) {
  const date = new Date(`${isoDate}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() - days);
  return date.toISOString().slice(0, 10);
}

function TrafficPanel({
  data,
  loading,
}: {
  data: ReturnType<typeof useReport>['data'];
  loading: boolean;
}) {
  if (loading) {
    return (
      <Card>
        <p className="typeBody">Loading traffic…</p>
      </Card>
    );
  }

  if (!data || data.quality.trafficUnavailable) {
    return (
      <EmptyState
        title="Traffic has not synced yet"
        body="Figures appear after the next analytics refresh."
      />
    );
  }

  const traffic = data.traffic;

  return (
    <>
      <Grid cols={4}>
        <KpiCard
          label="Visitors"
          value={traffic ? traffic.sessions.toLocaleString() : '—'}
          unit="sessions"
          meta="From referral links"
          provisional={data.quality.provisional}
          iconName="visitors"
        />
        {/* Engagement rate, not the prototype's bounce rate: GA4 replaced bounce with
            engagement and the two are complements, so labelling this figure "bounce"
            would show its opposite. */}
        <KpiCard
          label="Engagement rate"
          value={traffic ? `${(traffic.engagementRate * 100).toFixed(1)}` : '—'}
          unit="%"
          meta="Engaged sessions, GA4 definition"
          iconName="engagement"
          tint="pink"
        />
        <KpiCard
          label="Time on site"
          value={traffic ? formatDuration(traffic.averageSessionDuration) : '—'}
          meta="Average session"
          iconName="time"
          tint="ink"
        />
        <KpiCard
          label="Pages per session"
          value={traffic ? traffic.pagesPerSession.toFixed(1) : '—'}
          unit="pages"
          meta="Pages viewed per session"
          iconName="pages"
          tint="amber"
        />
      </Grid>

      <Card>
        <CardHeader
          title="Visitors"
          meta={
            peakOf(data.visitorBars, { hourly: data.from === data.to }) ??
            `${data.from} to ${data.to}`
          }
        />
        <BarChart
          bars={data.visitorBars}
          caption="Sessions over the selected period"
          emptyMessage="No sessions recorded in this period."
        />
      </Card>
    </>
  );
}

function formatDuration(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const rest = Math.round(seconds % 60);
  return minutes > 0 ? `${minutes}m ${String(rest).padStart(2, '0')}s` : `${rest}s`;
}
